import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { Helmet } from 'react-helmet';
import { firestore } from '../firebase.config';
import backgroundimg from "./../image/banner.png";
import Loader from './Loader';

const ServiceDetails = () => {
  const { serviceId } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerImageLoaded, setBannerImageLoaded] = useState(false);

  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        const docRef = doc(firestore, 'Service', serviceId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setService({ id: docSnap.id, ...docSnap.data() });
          // Initially assume banner image is not loaded
          setBannerImageLoaded(false);
          setShowBanner(true); // Show the banner container
        } else {
          console.log('No such document!');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching service details:', error);
        setLoading(false);
      } finally {
        // Set initial loading to false after attempting to fetch data
        if (!service) {
          setLoading(false);
        }
      }
    };

    fetchServiceDetails();
  }, [serviceId]);

  const handleBannerImageLoad = () => {
    setBannerImageLoaded(true);
    // Delay stopping the main loader after the banner image loads
    setTimeout(() => {
      setLoading(false);
    }, 500); // Adjust delay as needed
  };

  const handleBannerImageError = () => {
    // If the specific banner image fails, still stop the main loader
    setLoading(false);
  };

  const backgroundImageUrl = service?.bannerimageurl || backgroundimg;

  return (
    <div className="w-full h-auto bg-white flex flex-col z-0 lg:mt-20 mt-0">
      <Helmet>
        <title>{service?.title || 'Service'} - Service Details</title>
        <meta name="description" content={service?.description || ''} />
      </Helmet>

      {/* Loader while fetching initial data or if banner image is loading */}
      {loading ? (
        <div className="flex justify-center items-center min-h-screen">
          <Loader />
        </div>
      ) : (
        <>
          {/* Banner shown if showBanner is true */}
          {showBanner && (
            <div
              className="w-full h-[440px] bg-cover bg-center relative flex items-center justify-center"
              style={{
                backgroundImage: `url("${backgroundImageUrl}")`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {/* Show loader on top of banner if the specific banner image is not yet loaded */}
              {!bannerImageLoaded && service?.bannerimageurl ? (
                <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-10">
                  <Loader />
                </div>
              ) : null}
              <img
                src={backgroundImageUrl}
                alt={service?.title || 'Service Banner'}
                className="hidden" // Hide the actual image, background-image is used
                onLoad={handleBannerImageLoad}
                onError={handleBannerImageError}
              />
              <div className="relative z-0 flex justify-center lg:justify-start items-center w-full px-4 sm:px-10 lg:px-20">
                <div
                  className="opacity-0 translate-y-10 animate-slideUp bg-[#0c5495b3] p-6 shadow-lg text-white inline-block text-center lg:text-left max-w-[90%] sm:max-w-[80%] md:max-w-[60%] lg:max-w-[60%] lg:ml-14"
                  style={{
                    animation: "slideUp 1s ease-in-out forwards",
                    animationDelay: "0.5s",
                  }}
                >
                  <h1 className="text-[8vw] md:text-[50px] font-semibold mb-2 break-words">
                    Nexify {service?.title}
                  </h1>
                  <h2 className="text-[5vw] md:text-[40px] text-[#efefef]">
                    {service?.bannercontent}
                  </h2>
                </div>
              </div>
            </div>
          )}

          {/* Content only after full data is loaded and potentially banner image */}
          {service && !loading && (
            <div className="max-w-[1400px] px-4 mx-auto mt-20 mb-5 lg:px-20">
              <div className="w-full">
                <h1 className="text-3xl font-bold mb-4">{service?.title}</h1>
                <div
                  className="text-gray-700"
                  dangerouslySetInnerHTML={{ __html: service?.description }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ServiceDetails;